// Etsy kategori ozellikleri (malzeme, alici, durum, zincir tipi...).
//
// Etsy her kategori (taksonomi) icin kendi ozellik listesini verir; hepsi
// isteğe bagli ama aramadaki filtreler bunlardan beslenir. Doldurma sirasi:
//   1. kural   — urun verisinden kesin cikanlar (925 gumus -> Sterling silver)
//   2. sablon  — kategoride hep ayni olan tek degerler (Recipient: Women)
//   3. AI      — kalanlar icin izin verilen listeden secim (Occasion, Holiday...)
// Taslakta attributes.etsyProperties olarak durur; yayin (etsy-publish
// setListingProperties) varyasyona bagli olanlari atlayip Etsy'ye yazar.

import { useQueries, useQuery } from "@tanstack/react-query";
import { authHeaders, getBackendUrl } from "./http";
import {
  CUSTOM_PROPERTY_IDS, byName, makeProp, isEnumerated, maxValues,
  type EtsyTaxProp, type ListingProp,
} from "@erc/shared";

export {
  CUSTOM_PROPERTY_IDS, JEWELRY_PROPERTY_DEFAULTS, isEnumerated, maxValues, makeProp, byName,
  TEMPLATE_DEFAULTS_KEY, parseTemplateDefaults, serializeTemplateDefaults, templateProps,
  ruleProps, mergeProps, baseProps, effectiveTemplateDefaults,
} from "@erc/shared";
export type { EtsyTaxProp, ListingProp, RuleContext } from "@erc/shared";

async function fetchTaxonomyProps(taxonomyId: number): Promise<EtsyTaxProp[]> {
  const res = await fetch(`${getBackendUrl()}/api/etsy/listings/taxonomy/${taxonomyId}/properties`, { headers: authHeaders() });
  if (!res.ok) throw new Error(`Etsy kategori özellikleri alınamadı (${res.status})`);
  const body = await res.json();
  const rows = (body?.data?.results ?? body?.data ?? []) as EtsyTaxProp[];
  return rows.filter((p) => !CUSTOM_PROPERTY_IDS.includes(p.property_id));
}

const taxQuery = (taxonomyId: number | null | undefined) => ({
  queryKey: ["etsy-taxonomy-props", taxonomyId],
  queryFn: () => fetchTaxonomyProps(taxonomyId!),
  enabled: !!taxonomyId,
  staleTime: 6 * 60 * 60 * 1000, // kategori ozellikleri nadiren degisir
  retry: 1,
});

export function useTaxonomyProps(taxonomyId: number | null | undefined) {
  return useQuery(taxQuery(taxonomyId));
}

/** Birden fazla kategori (Liste Denetimi'nde her kart farkli olabilir) */
export function useTaxonomyPropsMap(taxonomyIds: Array<number | null | undefined>): Map<number, EtsyTaxProp[]> {
  const ids = [...new Set(taxonomyIds.filter((x): x is number => !!x))].sort();
  const results = useQueries({ queries: ids.map((id) => taxQuery(id)) });
  const map = new Map<number, EtsyTaxProp[]>();
  ids.forEach((id, i) => { const d = results[i]?.data; if (d) map.set(id, d); });
  return map;
}

/** AI: dolmamis secenekli ozellikler icin izin verilen listeden secim + 2 stil */
export async function aiProps(input: {
  taxProps: EtsyTaxProp[];
  filled: ListingProp[];
  productName?: string; title?: string; tags?: string[]; description?: string;
  attributes?: Record<string, string>;
  inspirations?: Array<{ title?: string | null; tags?: string[] }>;
}): Promise<{ props: ListingProp[]; styles: string[] }> {
  const filled = new Set(input.filled.map((p) => p.propertyId));
  const candidates = input.taxProps.filter((p) => isEnumerated(p) && !filled.has(p.property_id) && !/material features/i.test(p.name));
  const res = await fetch(`${getBackendUrl()}/api/listing-content/etsy-properties`, {
    method: "POST",
    headers: { ...authHeaders(), "Content-Type": "application/json" },
    body: JSON.stringify({
      productName: input.productName, title: input.title, tags: input.tags, description: input.description,
      attributes: input.attributes, inspirations: input.inspirations,
      properties: candidates.map((p) => ({ name: p.name, multi: !!p.is_multivalued, max: maxValues(p), values: p.possible_values!.map((v) => v.name) })),
    }),
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.error || `Özellikler üretilemedi (${res.status})`);
  const picks = (body?.data?.picks ?? {}) as Record<string, string[]>;
  const props = Object.entries(picks)
    .map(([name, values]) => { const t = byName(input.taxProps, name); return t ? makeProp(t, values, "ai") : null; })
    .filter((x): x is ListingProp => !!x);
  return { props, styles: (body?.data?.styles ?? []) as string[] };
}

/** Taslak attributes'undan okunan ozellikler */
export function draftPropsOf(attributes: unknown): ListingProp[] | null {
  const list = (attributes as { etsyProperties?: unknown } | null)?.etsyProperties;
  return Array.isArray(list) ? (list as ListingProp[]) : null;
}
export function draftStylesOf(attributes: unknown): string[] {
  const list = (attributes as { styles?: unknown } | null)?.styles;
  return Array.isArray(list) ? list.map(String) : [];
}
